import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useDebounce } from 'hooks/useDebounce';

import { stockListSelector } from 'store/stock/selectors';
import { priceTypesSelector } from 'store/auth/selectors';
import { feedbackListSelector, feedbackQuestionsListSelector } from 'store/feedback/selectors';
import { Feedback } from 'store/feedback/actions';
import { Stock } from 'store/stock/actions';
import { Auth } from 'store/auth/actions';

import { InputIcon } from 'components/Input';
import { Portal } from 'components/Portal';
import { Loader } from 'components/Loader';
import { InputRangeDatePicker } from 'components/InputRangeDatePicker/InputRangeDatePicker';
import { InputAutoComplete } from 'components/InputAutoComplete/InputAutoComplete';

import './style.scss';
import { useFirstRender } from 'hooks/useFirstRender';
import { generateExcel } from 'services/excel';
import { Button } from 'components/Button';

const header = ['Вариант ответа	', 'Кол-во	'];

const headerChanging = ['answer', 'answer_count'];

const ModalStatisticFeedback = ({ onClose }) => {
  const dispatch = useDispatch();

  const [calendarVisible, setCalendarVisible] = useState(false);

  const [stock, setStock] = useState(null);
  const [priceType, setPriceType] = useState(null);

  const [fromDate, setFrom] = useState(undefined);
  const [toDate, setTo] = useState(undefined);

  const priceTypeList = useSelector(priceTypesSelector);
  const feedback = useSelector(feedbackListSelector);
  const questions = useSelector(feedbackQuestionsListSelector);
  const stockList = useSelector(stockListSelector);
  const firstRender = useFirstRender();

  useEffect(() => {
    if (stockList === null) {
      dispatch(Stock.getStockList());
    }
    if (priceTypeList.length === 0) {
      dispatch(Auth.getPriceTypesList());
    }
    if (feedback === null) {
      dispatch(Feedback.getFeedbackList());
    }
    if (questions === null) {
      dispatch(Feedback.getQuestionsList());
    }
  }, [onClose]);

  useEffect(() => {
    if (firstRender) {
      return;
    }
    handleFilter();
  }, [priceType, stock, toDate, fromDate]);

  const handleGenerateExcel = () => {
    const nameStr = stock?.stockName || priceType?.typeName;

    const name = `${nameStr ? nameStr : 'Все склады'} ${new Date().toISOString()}`;
    generateExcel(feedback, name, header, headerChanging);
  };

  const handleStock = (value) => {
    setPriceType(null);
    setStock(value);
  };

  const handlePriceType = (value) => {
    setStock(null);
    setPriceType(value);
  };

  const handleFilter = () => {
    setCalendarVisible(false);
    const value = {};
    if (stock) {
      value.stock = stock.stockID1C;
    }

    if (priceType) {
      value.city = priceType.priceTypeID1C;
    }

    if (!fromDate & !toDate) {
      dispatch(Feedback.getFeedbackList(value));
    } else {
      dispatch(
        Feedback.getFeedbackList({
          ...value,
          endDateTime_min: Math.round(+fromDate / 1000),
          endDateTime_max: Math.round(+toDate / 1000),
        })
      );
    }
  };

  const handleFilterByDate = ({ from, to } = {}) => {
    if (from & to) {
      setTo(to);
      setFrom(from);
    }
    if (!from & !to) {
      setTo(undefined);
      setFrom(undefined);
    }
  };

  return (
    <Portal id="modal-statistic" className="feedback-statistic dropdownScroll" onClose={onClose}>
      <div className="modal-title">
        <div className="header-title-feedback-statistic">
          <b>Статистика отзывов клиентов</b> <Button onClick={handleGenerateExcel}>Импорт</Button>
        </div>
      </div>
      <div className="feedback-filter-modal">
        <div className="instruments-search-panel">
          <InputAutoComplete
            icon="search"
            selected={{ value: stock?.stockID1C || '', text: stock?.stockName || '' }}
            list={stockList.map((stockElem) => ({
              text: stockElem.stockName,
              value: stockElem.stockID1C,
              returning: stockElem,
            }))}
            size="90px"
            onChange={handleStock}
            placeholder="Склад..."
          />
        </div>
        <div className="instruments-search-panel">
          <InputAutoComplete
            icon="search"
            selected={{ value: priceType?.priceTypeID1C || '', text: priceType?.typeName || '' }}
            list={priceTypeList.map((elem) => ({
              text: elem.typeName,
              value: elem.priceTypeID1C,
              returning: elem,
            }))}
            size="90px"
            onChange={handlePriceType}
            placeholder="Город..."
          />
        </div>
        <div className="instruments-search-panel">
          <InputRangeDatePicker
            handleFilter={handleFilterByDate}
            boxShadow={false}
            relative={true}
            close={() => setCalendarVisible(false)}
          />
        </div>
      </div>
      <div className="table-feedback-container dropdownScroll">
        <table className="table-feedback dropdownScroll">
          <thead>
            <tr className="table-feedback-tr">
              <th className="feedback-header" style={{ width: '1000px', textAlign: 'left' }}>
                Вариант ответа
              </th>
              <th className="feedback-header" style={{ width: '100px', textAlign: 'center' }}>
                Кол-во
              </th>
              <th className="feedback-header" style={{ width: '20px', textAlign: 'left' }}></th>
            </tr>
          </thead>
          <tbody className="body-feedback-list dropdownScroll">
            {feedback &&
              feedback.map((element) => {
                return (
                  <tr key={element.answer}>
                    <th className="feedback-row text-light" style={{ width: '1000px', textAlign: 'left' }}>
                      {element.isQuestion ? <b>{element.answer}</b> : <>{element.answer}</>}
                    </th>
                    <th className="feedback-row text-light" style={{ width: '100px', textAlign: 'center' }}>
                      {element.isQuestion ? <b>{element.answer_count}</b> : <>{element.answer_count}</>}
                    </th>
                    <th className="feedback-row text-light" style={{ width: '20px', textAlign: 'left' }}></th>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </Portal>
  );
};

ModalStatisticFeedback.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export { ModalStatisticFeedback };
